import React, {useState} from "react";
import {Form, Modal} from "react-bootstrap";
import {useFormik} from "formik";
import * as Yup from "yup";
import {defaultClientExpense} from "../generic/InitialStates";
import {AGCText} from "../../generic-components/AGCText";
import {AGCTextArea} from "../../generic-components/AGCTextArea";
import {AGCSelect} from "../../generic-components/AGCSelect";
import {apiGenericCreate} from "../generic/crud";
import {ENTITY_ID_CLIENT_EXPENSE, ENTITY_ID_EXPENSE_CATEGORY, ENTITY_ID_LEDGER_ACCOUNT} from "../generic/Constants";
import {formatDateTime} from "../../utils/Utils";
import {showToast, TOAST_SUCCESS, TOAST_FAILURE} from "../../utils/toast/Toast";

export function ExpenseCreateDialog({show, handleClose}) {
    const [isLoading, setIsLoading] = useState(false);

    const validationSchema = Yup.object({
        totalAmount: Yup.number()
            .required("Amount is required")
            .positive("Amount must be greater than 0"),
        expenseDate: Yup.string().required("Expense date is required"),
        category: Yup.object().nullable().required("Category is required"),
        ledgerAccount: Yup.object().nullable().required("Paid From account is required"),
    });

    const formik = useFormik({
        initialValues: defaultClientExpense,
        validationSchema: validationSchema,
        enableReinitialize: true,
        onSubmit(values, {resetForm}) {
            setIsLoading(true);

            const expense = {
                totalAmount: Number(values.totalAmount),
                expenseDate: values.expenseDate,
                categoryId: values.category?.id,
                categoryName: values.category?.name,
                ledgerAccountId: values.ledgerAccount?.id,
                ledgerAccountName: values.ledgerAccount?.name,
                narration: values.narration,
                referenceNumber: values.referenceNumber,
            };

            apiGenericCreate(ENTITY_ID_CLIENT_EXPENSE, expense).then(() => {
                setIsLoading(false);
                handleClose();
                showToast("Expense created!", TOAST_SUCCESS);
                resetForm();
            }).catch((error) => {
                setIsLoading(false);
                showToast(
                    "Error creating expense: " + (error.message || "Unknown error"),
                    TOAST_FAILURE
                );
            });
        }
    });

    return (
        <Modal
            size="lg"
            show={show}
            onHide={handleClose}
            aria-labelledby="expense-create-dialog">
            <Modal.Header closeButton>
                <Modal.Title>New Expense</Modal.Title>
            </Modal.Header>
            <Modal.Body className="overlay">
                {!isLoading ? (
                    <>
                        <div className="alert alert-info d-flex align-items-center mb-4">
                            <i className="fas fa-info-circle me-3 fs-4"></i>
                            <div>
                                <strong>Create Expense</strong>
                                <br/>
                                <small>
                                    Record an expense with category and payment account.
                                    <strong> Sync categories from server first</strong> if the category list is empty.
                                    Expenses will be synced to the server as vouchers.
                                </small>
                            </div>
                        </div>
                        <Form className="form form-label-right" onSubmit={formik.handleSubmit}>
                            <div className="form-group row mb-3">
                                <Form.Group className="col-lg-6">
                                    <AGCText
                                        formik={formik}
                                        name={"Amount"}
                                        placeHolder={"Enter Amount"}
                                        id={"totalAmount"}
                                        toolTip={"Enter the expense amount"}
                                        value={formik.values.totalAmount}
                                        required={true}
                                        validation={[0]}
                                    />
                                </Form.Group>

                                <Form.Group className="col-lg-6">
                                    <Form.Label className="required">Expense Date</Form.Label>
                                    <Form.Control
                                        type="text"
                                        id="expenseDate"
                                        value={formatDateTime(formik.values.expenseDate)}
                                        readOnly
                                        disabled
                                        className="bg-light text-muted"
                                    />
                                    <Form.Text className="text-muted">Current date & time (auto-set)</Form.Text>
                                </Form.Group>
                            </div>

                            <div className="form-group row mb-3">
                                <Form.Group className="col-lg-6">
                                    <AGCSelect
                                        entityId={ENTITY_ID_EXPENSE_CATEGORY}
                                        formik={formik}
                                        name={"Category"}
                                        id={"category"}
                                        toolTip={"Select expense category"}
                                        value={formik.values.category}
                                        required={true}
                                    />
                                    {formik.touched.category && formik.errors.category && (
                                        <div className="text-danger small mt-1">{formik.errors.category}</div>
                                    )}
                                </Form.Group>

                                <Form.Group className="col-lg-6">
                                    <AGCSelect
                                        entityId={ENTITY_ID_LEDGER_ACCOUNT}
                                        formik={formik}
                                        name={"Paid From"}
                                        id={"ledgerAccount"}
                                        toolTip={"Select cash/bank account"}
                                        value={formik.values.ledgerAccount}
                                        required={true}
                                    />
                                    {formik.touched.ledgerAccount && formik.errors.ledgerAccount && (
                                        <div className="text-danger small mt-1">{formik.errors.ledgerAccount}</div>
                                    )}
                                </Form.Group>
                            </div>

                            <div className="form-group row mb-3">
                                <Form.Group className="col-lg-6">
                                    <AGCText
                                        formik={formik}
                                        name={"Reference Number"}
                                        placeHolder={"Enter Reference Number (Optional)"}
                                        id={"referenceNumber"}
                                        toolTip={"Optional reference number"}
                                        value={formik.values.referenceNumber}
                                        required={false}
                                    />
                                </Form.Group>
                            </div>

                            <div className="form-group row mb-3">
                                <Form.Group className="col-lg-12">
                                    <AGCTextArea
                                        formik={formik}
                                        name={"Narration"}
                                        placeHolder={"Enter expense description (Optional)"}
                                        id={"narration"}
                                        toolTip={"Describe the expense"}
                                        value={formik.values.narration}
                                        rows={3}
                                        required={false}
                                    />
                                </Form.Group>
                            </div>

                            <Modal.Footer>
                                <div>
                                    <button
                                        type="button"
                                        className="btn btn-light btn-elevate"
                                        onClick={handleClose}>
                                        Cancel
                                    </button>
                                    <> </>
                                    <button
                                        type="submit"
                                        className="btn btn-primary btn-elevate">
                                        Save
                                    </button>
                                </div>
                            </Modal.Footer>
                        </Form>
                    </>
                ) : (
                    <div className="text-center p-4">
                        <span>Please Wait, Saving Expense...</span>
                    </div>
                )}
            </Modal.Body>
        </Modal>
    );
}
